import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Card,
  CardBody,
  Table,
} from 'reactstrap';
import 'react-datepicker/dist/react-datepicker.css';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import axios from 'axios';
import { api } from '../../config';
import notify, { MESSAGES } from '../../utils/notify';
import { buttonProps } from '../../utils/a11y';
import { confirmDelete } from '../../utils/confirm';
import logger from '../../utils/logger';
import ActionButton from '../../Components/Common/ActionButton';
import { EmptyState } from '../../Components/Common/ListStates';
import { SkeletonText } from '../../Components/Common/Skeleton';
import {
  FormField,
  FormGrid,
  RequiredNote,
  TextField,
} from '../../Components/Form';
import { formatDate, formatFileSize } from '../../utils/format';
import StepActionBar from './component/StepActionBar';
import './component/procurementDetail.css';
import { focusFieldById } from './utils/focusField';

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

const SupportDocumentTab = ({
  goNext,
  goBack,
  ids,
  form,
  _setForm,
  handleSaveCompleteDoc,
  token,
  setIsLoading,
  _isLoading,
}) => {
  const [uploadDocument, setUploadDocument] = useState({ items: [] });
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [fileCategory, setFileCategory] = useState({ items: [] });
  const [localLoading, setLocalLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [masterDocModal, setMasterDocModal] = useState(false);
  const [selectedMasterDocIds, setSelectedMasterDocIds] = useState([]);
  const [uploadModal, setUploadModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [customFileName, setCustomFileName] = useState('');
  const [errors, setErrors] = useState({});

  const fetchDocumentUpload = useCallback(
    async procurementId => {
      try {
        const response = await fetch(
          `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${procurementId}`,
          {
            params: { ProcurementId: procurementId },
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await response.json();
        const items = result.data.items.map(item => ({
          id: item.id ?? null,
          customFileName: item.customFileName
            ? item.customFileName.replace(/\.[^/.]+$/, '')
            : null,
          documentName: item.documentUpload?.documentName ?? null,
          documentUploadId: item.documentUploadId ?? null,
          actualUrl: item.link ?? null,
          isMasterDocument: !!item.documentUploadId,
          isShowToDMUT: item.isShowToDMUT ?? false,
          lastUpdated: item.updatedDate ? formatDate(item.updatedDate) : null,
          firstCreated: item.createdDate ? formatDate(item.createdDate) : null,
        }));
        setUploadDocument({ items });
        setErrors(prev => ({ ...prev, uploadDocument: null }));
        setDocsLoaded(true);
      } catch (error) {
        logger.error('Error fetching data from Invoice API:', error);
      }
    },
    [token]
  );

  const fetchFileCategory = useCallback(async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/document-upload/getall-master-procurement-document`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.documentName,
        actualUrl: item.path ?? null,
      }));
      setFileCategory({ items });
    } catch (error) {
      logger.error('Error fetching file categories:', error);
    }
  }, [token]);

  useEffect(() => {
    if (ids) {
      fetchDocumentUpload(ids);
      fetchFileCategory();
    }
  }, [ids, fetchDocumentUpload, fetchFileCategory]);

  const handleOpenMasterDocModal = () => {
    setSelectedMasterDocIds([]);
    setMasterDocModal(true);
  };

  const handleMasterDocCheckboxToggle = id => {
    setSelectedMasterDocIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleMasterDocSelectAll = () => {
    const availableItems = fileCategory.items.filter(
      item =>
        !uploadDocument.items.some(doc => doc.documentUploadId === item.value)
    );
    const allIds = availableItems.map(item => item.value);
    setSelectedMasterDocIds(prev =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleAddMasterDoc = async () => {
    if (selectedMasterDocIds.length === 0) return;
    setLocalLoading(true);
    try {
      const payload = {
        procurementId: ids,
        documentUploadIds: selectedMasterDocIds,
      };

      await axios.put(
        `${api.API_URL}/procurement-document/select-master-document`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(
        `${selectedMasterDocIds.length} document(s) added successfully.`
      );
      setMasterDocModal(false);
      setSelectedMasterDocIds([]);
      fetchDocumentUpload(ids);
    } catch (error) {
      notify.apiError(error, 'add document(s)');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleFileSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingFile(file);
    setCustomFileName('');
    setUploadModal(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    setPendingFile(file);
    setCustomFileName('');
    setUploadModal(true);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleUploadFile = async () => {
    if (!pendingFile || !customFileName.trim()) return;
    setLocalLoading(true);
    try {
      const formData = new FormData();
      formData.append('ProcurementId', ids);
      formData.append('FileName', customFileName.trim());
      formData.append('FileAttachment', pendingFile);
      await axios.post(
        `${api.API_URL}/procurement-document/custom-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('File uploaded successfully.');
      setUploadModal(false);
      setPendingFile(null);
      setCustomFileName('');
      fetchDocumentUpload(ids);
    } catch (error) {
      notify.apiError(error, 'upload file');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleToggleDmut = async (id, checked) => {
    setUploadDocument(prev => ({
      items: prev.items.map(item =>
        item.id === id ? { ...item, isShowToDMUT: checked } : item
      ),
    }));
    try {
      await axios.put(
        `${api.API_URL}/procurement-document/change-is-show-to-dmut/${id}`,
        { isShowToDmut: checked },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      setUploadDocument(prev => ({
        items: prev.items.map(item =>
          item.id === id ? { ...item, isShowToDMUT: !checked } : item
        ),
      }));
      notify.apiError(error, 'update Registered Supplier visibility');
    }
  };

  const handleDirectOpenNewTab = url => {
    if (!url) {
      notify.info('There is no file available for this document.');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmDelFileUpload = async (id, subject) => {
    if (!(await confirmDelete('document', subject))) return;
    await handleDelFileUpload(id);
  };

  const handleDelFileUpload = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/procurement-document/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Data deleted successfully.');
    } catch (error) {
      notify.apiError(error, 'delete data');
    } finally {
      setIsLoading(false);
      fetchDocumentUpload(ids);
    }
  };

  // Validation mirroring validatequantityanddelivery on RequirementTab:
  // it must pass before the "Complete supporting documents?" confirmation
  // is shown by handleSaveCompleteDoc.
  const validateSupportDocument = () => {
    // Documents are still being fetched, nothing to validate yet.
    if (!docsLoaded) return true;

    if (!uploadDocument.items || uploadDocument.items.length === 0) {
      setErrors(prev => ({
        ...prev,
        uploadDocument: 'Please add at least one supporting document.',
      }));
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    setErrors(prev => ({ ...prev, uploadDocument: null }));
    return true;
  };

  const handleSaveCompleteDocWithValidation = async () => {
    if (!validateSupportDocument()) {
      // Point the user at where documents are added.
      window.setTimeout(() => focusFieldById('sd-add-master'), 60);
      return;
    }
    const completed = await handleSaveCompleteDoc();
    if (completed) goNext?.();
  };

  const isLocked = form.isProcurementCompleted !== false;
  const isStepDone = form.isSupportingDocumentsCompleted === true;
  const visibleCount = uploadDocument.items.filter(
    doc => doc.isShowToDMUT
  ).length;

  return (
    <React.Fragment>
      <div className='pd-step-head'>
        <div>
          <h5>
            <i className='ri-file-text-line me-2'></i>
            Supporting Documents
          </h5>
          <p>
            Documents switched to visible are sent to the invited suppliers with
            the tender.
          </p>
        </div>
        <div className='pd-counters' aria-live='polite'>
          <div className='pd-counter'>
            <strong>{docsLoaded ? uploadDocument.items.length : '–'}</strong>
            <span>documents</span>
          </div>
          <div className='pd-counter'>
            <strong>{docsLoaded ? visibleCount : '–'}</strong>
            <span>visible to suppliers</span>
          </div>
        </div>
      </div>

      <Card className='border shadow-sm'>
        <CardBody>
          <div>
            <div className='mb-3'>
              <div>
                <Row>
                  <Col
                    md={3}
                    style={{
                      pointerEvents:
                        form.isProcurementCompleted !== false ? 'none' : 'auto',
                      opacity: form.isProcurementCompleted !== false ? 0.5 : 1,
                    }}
                  >
                    <h6
                      className='text-uppercase fw-semibold mb-3'
                      style={{
                        color: 'var(--vz-heading-color, #1a1a2e)',
                        fontSize: '12px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Upload Document
                    </h6>
                    <div
                      id='sd-add-master'
                      className='sd-upload-card mb-3'
                      {...buttonProps(
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleOpenMasterDocModal,
                        undefined,
                        {
                          'aria-disabled':
                            form.isProcurementCompleted !== false || undefined,
                        }
                      )}
                    >
                      <div className='sd-upload-card-icon master'>
                        <i
                          className='ri-database-2-line'
                          aria-hidden='true'
                        ></i>
                      </div>
                      <div className='sd-upload-card-text'>
                        <h6>Master Document</h6>
                        <p>Select from master database</p>
                      </div>
                    </div>
                    <div
                      className={`sd-upload-card${dragOver ? ' drag-over' : ''}`}
                      onDragOver={
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleDragOver
                      }
                      onDragLeave={
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleDragLeave
                      }
                      onDrop={
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleDrop
                      }
                      {...buttonProps(
                        () => {
                          if (form.isProcurementCompleted !== false) return;
                          fileInputRef.current?.click();
                        },
                        undefined,
                        {
                          'aria-disabled':
                            form.isProcurementCompleted !== false || undefined,
                        }
                      )}
                    >
                      <div className='sd-upload-card-icon custom'>
                        <i
                          className='ri-upload-cloud-2-line'
                          aria-hidden='true'
                        ></i>
                      </div>
                      <div className='sd-upload-card-text'>
                        <h6>Upload Custom File</h6>
                        <p>Browse from your device</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type='file'
                        hidden
                        accept='.pdf,.doc,.docx,.xls,.xlsx'
                        onChange={handleFileSelect}
                      />
                    </div>
                  </Col>

                  <Col md={9}>
                    <div>
                      <h6
                        className='text-uppercase fw-semibold mb-3'
                        style={{
                          color: 'var(--vz-heading-color, #1a1a2e)',
                          fontSize: '12px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Selected / Uploaded Document
                      </h6>
                      {errors.uploadDocument && (
                        <div
                          className='text-danger d-flex align-items-center gap-1 mb-2'
                          style={{ fontSize: '12px' }}
                          role='alert'
                        >
                          <i
                            className='ri-error-warning-line'
                            aria-hidden='true'
                          ></i>
                          <span>{errors.uploadDocument}</span>
                        </div>
                      )}
                      {!docsLoaded ? (
                        <SkeletonText lines={4} />
                      ) : uploadDocument.items.length === 0 ? (
                        <EmptyState
                          compact
                          icon='ri-file-text-line'
                          title='No documents yet'
                          emptyHint={
                            form.isProcurementCompleted === false
                              ? 'No supporting documents have been added to this procurement. Pick from the master list or upload your own file.'
                              : 'No supporting documents were added to this procurement before it was completed.'
                          }
                          action={
                            form.isProcurementCompleted === false
                              ? {
                                  label: 'Upload custom file',
                                  icon: 'ri-upload-cloud-2-line',
                                  onClick: () => fileInputRef.current?.click(),
                                }
                              : null
                          }
                          secondaryAction={
                            form.isProcurementCompleted === false
                              ? {
                                  label: 'Add master document',
                                  icon: 'ri-database-2-line',
                                  onClick: handleOpenMasterDocModal,
                                }
                              : null
                          }
                        />
                      ) : (
                        <>
                          <div className='sd-doc-header'>
                            <span style={{ flex: '2 1 0' }}>Document Name</span>
                            <span style={{ flex: '1 1 0' }}>Source</span>
                            <span style={{ flex: '1 1 0' }}>Last Updated</span>
                            <span
                              style={{ flex: '1 1 0', textAlign: 'center' }}
                            >
                              Show to Registered Supplier?
                            </span>
                            <span
                              style={{ flex: '1.4 1 0', textAlign: 'right' }}
                            >
                              Action
                            </span>
                          </div>
                          {uploadDocument.items.map((doc, index) => (
                            <div key={doc.id || index} className='sd-doc-row'>
                              <div style={{ flex: '2 1 0' }}>
                                <span
                                  className='fw-semibold'
                                  style={{
                                    fontSize: '13px',
                                    color: 'var(--vz-heading-color, #1a1a2e)',
                                  }}
                                >
                                  {doc.documentName ||
                                    doc.customFileName ||
                                    '-'}
                                </span>
                              </div>
                              <div style={{ flex: '1 1 0' }}>
                                <span
                                  className={`sd-badge ${doc.isMasterDocument ? 'master' : 'custom'}`}
                                >
                                  {doc.isMasterDocument
                                    ? 'Master Document'
                                    : 'Upload Custom'}
                                </span>
                              </div>
                              <div style={{ flex: '1 1 0' }}>
                                <span
                                  style={{
                                    fontSize: '12px',
                                    color: 'var(--vz-secondary-color, #6b7280)',
                                  }}
                                >
                                  {doc.lastUpdated || doc.firstCreated || 'N/A'}
                                </span>
                              </div>

                              <div
                                className='d-flex justify-content-center form-check form-switch form-switch-secondary mb-2'
                                style={{ flex: '1 1 0' }}
                              >
                                <div className='flex-shrink-0'>
                                  <Input
                                    className='form-check-input'
                                    type='checkbox'
                                    aria-label={`Show ${
                                      doc.documentName ||
                                      doc.customFileName ||
                                      'document'
                                    } to registered suppliers`}
                                    checked={doc.isShowToDMUT || false}
                                    onChange={e =>
                                      handleToggleDmut(doc.id, e.target.checked)
                                    }
                                    disabled={
                                      form.isProcurementCompleted !== false
                                    }
                                  />
                                </div>
                              </div>

                              <div
                                style={{ flex: '1.4 1 0', textAlign: 'right' }}
                              >
                                <div className='d-flex gap-1 justify-content-end'>
                                  <ActionButton
                                    variant='tertiary'
                                    size='sm'
                                    icon='ri-eye-line'
                                    onClick={() =>
                                      handleDirectOpenNewTab(doc.actualUrl)
                                    }
                                  >
                                    View
                                  </ActionButton>
                                  {form.isProcurementCompleted === false && (
                                    <ActionButton
                                      variant='destructive'
                                      size='sm'
                                      icon='ri-delete-bin-line'
                                      confirm={false}
                                      onClick={() =>
                                        handleConfirmDelFileUpload(
                                          doc.id,
                                          doc.documentName || doc.customFileName
                                        )
                                      }
                                    >
                                      Delete
                                    </ActionButton>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      {/* Adding, removing and the visibility switch save immediately, so
          this step has no draft button. */}
      <StepActionBar
        status={
          isLocked
            ? 'Read-only after submission'
            : 'Changes to documents save automatically'
        }
        statusTone={isLocked ? 'muted' : 'ok'}
        statusIcon={isLocked ? 'ri-lock-line' : 'ri-checkbox-circle-line'}
      >
        <ActionButton
          variant='secondary'
          icon='ri-arrow-left-line'
          onClick={() => goBack?.()}
        >
          Requirement
        </ActionButton>
        {isLocked || isStepDone ? (
          <ActionButton
            variant='primary'
            icon='ri-arrow-right-line'
            onClick={() => goNext?.()}
          >
            Next: GTC
          </ActionButton>
        ) : (
          <ActionButton
            variant='primary'
            icon='ri-check-double-line'
            onClick={handleSaveCompleteDocWithValidation}
          >
            Complete & Continue
          </ActionButton>
        )}
      </StepActionBar>

      {/* MASTER DOCUMENT MODAL */}
      <Modal
        isOpen={masterDocModal}
        toggle={() => setMasterDocModal(false)}
        centered
        size='md'
      >
        <ModalHeader
          toggle={() => setMasterDocModal(false)}
          className='fw-semibold'
          style={{ borderBottom: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <i
            className='ri-database-2-line me-2'
            style={{ color: '#0d7c66' }}
          ></i>
          Select Master Document
        </ModalHeader>
        <ModalBody className='py-4'>
          <FormField group label='Document category' name='masterDocuments'>
            <div
              className='table-responsive'
              style={{ maxHeight: '300px', overflowY: 'auto' }}
            >
              {(() => {
                const availableMasterDocs = fileCategory.items.filter(
                  item =>
                    !uploadDocument.items.some(
                      doc => doc.documentUploadId === item.value
                    )
                );
                if (availableMasterDocs.length === 0) {
                  return (
                    <EmptyState
                      compact
                      icon='ri-database-2-line'
                      title='No master documents available'
                      emptyHint={
                        fileCategory.items.length === 0
                          ? 'No master documents have been set up yet.'
                          : 'Every master document has already been added to this procurement.'
                      }
                    />
                  );
                }
                return (
                  <Table className='align-middle table-nowrap mb-0' size='sm'>
                    <thead className='table-light'>
                      <tr>
                        <th className='text-center' style={{ width: '50px' }}>
                          <div className='form-check'>
                            <Input
                              className='form-check-input'
                              type='checkbox'
                              aria-label='Select all documents'
                              checked={
                                availableMasterDocs.length > 0 &&
                                selectedMasterDocIds.length ===
                                  availableMasterDocs.length
                              }
                              ref={el => {
                                if (el)
                                  el.indeterminate =
                                    selectedMasterDocIds.length > 0 &&
                                    selectedMasterDocIds.length <
                                      availableMasterDocs.length;
                              }}
                              onChange={handleMasterDocSelectAll}
                            />
                          </div>
                        </th>
                        <th>Document Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableMasterDocs.map(item => (
                        <tr key={item.value}>
                          <td className='text-center'>
                            <div className='form-check'>
                              <Input
                                className='form-check-input'
                                type='checkbox'
                                aria-label={`Select ${item.label}`}
                                checked={selectedMasterDocIds.includes(
                                  item.value
                                )}
                                onChange={() =>
                                  handleMasterDocCheckboxToggle(item.value)
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center gap-2'>
                              <i
                                className='ri-folder-2-line'
                                style={{ color: '#f59e0b', fontSize: '16px' }}
                              ></i>
                              <span className='flex-grow-1'>{item.label}</span>
                              <ActionButton
                                variant='tertiary'
                                size='sm'
                                icon='ri-eye-line'
                                onClick={() =>
                                  handleDirectOpenNewTab(item.actualUrl)
                                }
                                title='View'
                              >
                                View
                              </ActionButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                );
              })()}
            </div>
          </FormField>
        </ModalBody>
        <ModalFooter
          style={{ borderTop: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <ActionButton
            variant='secondary'
            size='sm'
            icon='ri-close-line'
            onClick={() => setMasterDocModal(false)}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            size='sm'
            icon='ri-database-2-line'
            disabled={selectedMasterDocIds.length === 0}
            loading={localLoading}
            onClick={handleAddMasterDoc}
          >
            {localLoading ? 'Adding...' : 'Add Document'}
          </ActionButton>
        </ModalFooter>
      </Modal>

      {/* UPLOAD CATEGORY MODAL */}
      <Modal
        isOpen={uploadModal}
        toggle={() => setUploadModal(false)}
        centered
        size='md'
      >
        <ModalHeader
          toggle={() => setUploadModal(false)}
          className='fw-semibold'
          style={{ borderBottom: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <i
            className='ri-upload-cloud-2-line me-2'
            style={{ color: '#e8860c' }}
          ></i>
          Upload Document
        </ModalHeader>
        <ModalBody className='py-4'>
          <RequiredNote />
          <FormGrid columns={1}>
            <TextField
              label='Selected file'
              name='pendingFileName'
              mode='readonly'
              value={pendingFile?.name || ''}
              hint={pendingFile ? formatFileSize(pendingFile.size) : ''}
            />
            <TextField
              label='Document name'
              name='customFileName'
              required
              placeholder='e.g. Technical specification'
              value={customFileName}
              onChange={e => setCustomFileName(e.target.value)}
            />
          </FormGrid>
        </ModalBody>
        <ModalFooter
          style={{ borderTop: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <ActionButton
            variant='secondary'
            size='sm'
            icon='ri-close-line'
            onClick={() => setUploadModal(false)}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            size='sm'
            icon='ri-upload-cloud-2-line'
            disabled={!customFileName.trim()}
            loading={localLoading}
            onClick={handleUploadFile}
          >
            {localLoading ? 'Uploading...' : 'Upload'}
          </ActionButton>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SupportDocumentTab;
